<!-- components/courses/CourseStatistics.vue -->
<template>
  <div class="course-statistics">
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">🎬</div>
        <div class="stat-info">
          <h3>{{ totalVideos }}</h3>
          <p>Video Modules</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">📄</div>
        <div class="stat-info">
          <h3>{{ totalArticles }}</h3>
          <p>Article Modules</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">⏱️</div>
        <div class="stat-info">
          <h3>{{ totalDuration }}</h3>
          <p>Total Video Hours</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">📚</div>
        <div class="stat-info">
          <h3>{{ totalModules }}</h3>
          <p>Total Modules</p>
        </div>
      </div>
    </div>

    <div class="stat-details">
      <div class="detail-section">
        <h4>Section Breakdown</h4>
        <div class="section-list">
          <div
            v-for="section in course.sections"
            :key="section.id"
            class="section-stat"
          >
            <span class="section-name">{{ section.title }}</span>
            <span class="section-count"
              >{{ section.modules.length }} modules</span
            >
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h4>Content Summary</h4>
        <div class="summary-grid">
          <div class="summary-item">
            <span class="label">Total Sections</span>
            <span class="value">{{ course.sections?.length || 0 }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Published Modules</span>
            <span class="value">{{ publishedModules }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Draft Modules</span>
            <span class="value">{{ draftModules }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Completion Rate</span>
            <span class="value">{{ completionRate }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  course: {
    type: Object,
    required: true,
  },
});

const totalVideos = computed(() => {
  let count = 0;
  props.course.sections?.forEach((section) => {
    section.modules?.forEach((module) => {
      if (module.type === "video") count++;
    });
  });
  return count;
});

const totalArticles = computed(() => {
  let count = 0;
  props.course.sections?.forEach((section) => {
    section.modules?.forEach((module) => {
      if (module.type === "article") count++;
    });
  });
  return count;
});

const totalModules = computed(() => {
  let count = 0;
  props.course.sections?.forEach((section) => {
    count += section.modules?.length || 0;
  });
  return count;
});

const totalDuration = computed(() => {
  let totalSeconds = 0;
  props.course.sections?.forEach((section) => {
    section.modules?.forEach((module) => {
      if (module.type === "video" && module.content?.duration) {
        const parts = module.content.duration.split(":");
        totalSeconds += parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
    });
  });

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
});

const publishedModules = computed(() => {
  let count = 0;
  props.course.sections?.forEach((section) => {
    section.modules?.forEach((module) => {
      if (module.isPublished) count++;
    });
  });
  return count;
});

const draftModules = computed(() => {
  let count = 0;
  props.course.sections?.forEach((section) => {
    section.modules?.forEach((module) => {
      if (!module.isPublished) count++;
    });
  });
  return count;
});

const completionRate = computed(() => {
  if (totalModules.value === 0) return 0;
  return Math.round((publishedModules.value / totalModules.value) * 100);
});
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;

.course-statistics {
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 32px;

    .stat-card {
      background: #f8f9fa;
      border-radius: 12px;
      padding: 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      border: 1px solid #e9ecef;

      .stat-icon {
        font-size: 32px;
      }

      .stat-info {
        h3 {
          margin: 0;
          font-size: 24px;
          color: #1a1a1a;
        }

        p {
          margin: 0;
          font-size: 14px;
          color: #868e96;
        }
      }
    }
  }

  .stat-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    .detail-section {
      background: #fff;
      border-radius: 12px;
      padding: 20px;
      border: 1px solid #e9ecef;

      h4 {
        margin: 0 0 16px 0;
        color: #1a1a1a;
        font-size: 16px;
      }

      .section-list {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .section-stat {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #f1f3f5;

          .section-name {
            color: #495057;
          }

          .section-count {
            color: #1a1a1a;
            font-weight: 500;
          }
        }
      }

      .summary-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;

        .summary-item {
          display: flex;
          flex-direction: column;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 8px;

          .label {
            font-size: 12px;
            color: #868e96;
          }

          .value {
            font-size: 20px;
            font-weight: 700;
            color: #1a1a1a;
          }
        }
      }
    }
  }
}
</style>
